/**
 * SocialShareButtons - Social media sharing buttons for referral links
 * Instagram shows a confirmation modal (explains copy-paste flow)
 * Facebook and LinkedIn execute directly (have native share dialogs)
 */
import React, { useState } from "react";
import { Button, Group, Modal, Stack, Text } from "@mantine/core";
import { useClipboard } from "@mantine/hooks";
import {
  IconBrandFacebook,
  IconBrandInstagram,
  IconBrandLinkedin,
  IconCheck,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { openNewTab } from "@/utils/browserUtils";

interface SocialShareButtonsProps {
  referralLink: string;
}

export const SocialShareButtons: React.FC<SocialShareButtonsProps> = ({
  referralLink,
}) => {
  const clipboard = useClipboard({ timeout: 2000 });
  const [instagramModalOpen, setInstagramModalOpen] = useState(false);

  // Helper function to convert localhost URLs to production URLs for sharing
  const getShareableLink = (link: string): string => {
    if (link.includes("localhost")) {
      // Use production domain for localhost testing (social media can't access localhost)
      return "https://smartwiz.ai";
    }
    return link;
  };

  // Helper to open share dialogs with a small delay for better UX
  const openShareDialog = (url: string, features?: string) => {
    setTimeout(() => {
      openNewTab(url, features);
    }, 300);
  };

  // Facebook - executes directly without modal
  const handleFacebookClick = () => {
    const shareableLink = getShareableLink(referralLink);
    const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareableLink)}`;
    openShareDialog(shareUrl, "width=800,height=600");
  };

  // LinkedIn - executes directly without modal
  const handleLinkedInClick = () => {
    const shareableLink = getShareableLink(referralLink);
    const shareUrl = `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareableLink)}`;
    openShareDialog(shareUrl, "width=800,height=600");
  };

  // Instagram - opens confirmation modal first
  const handleInstagramClick = () => {
    setInstagramModalOpen(true);
  };

  const executeInstagramShare = () => {
    const shareableLink = getShareableLink(referralLink);
    clipboard.copy(shareableLink);
    notifications.show({
      title: "Link Copied!",
      message: "Paste this link in your Instagram post or story",
      color: "green",
      icon: <IconCheck size={16} />,
      autoClose: 5000,
    });
    setInstagramModalOpen(false);

    // Open Instagram after a delay so user sees the notification
    setTimeout(() => {
      openNewTab("https://www.instagram.com");
    }, 800);
  };

  if (!referralLink) return null;

  return (
    <>
      <Group justify="center" gap="xl">
        <Stack
          gap={4}
          align="center"
          style={{
            cursor: "pointer",
            transition: "transform 0.2s ease, opacity 0.2s ease",
          }}
          onClick={handleFacebookClick}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "scale(1.1)";
            e.currentTarget.style.opacity = "0.8";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
            e.currentTarget.style.opacity = "1";
          }}
        >
          <IconBrandFacebook size={48} color="#1877F2" />
          <Text size="xs" fw={500}>
            Facebook
          </Text>
        </Stack>
        <Stack
          gap={4}
          align="center"
          style={{
            cursor: "pointer",
            transition: "transform 0.2s ease, opacity 0.2s ease",
          }}
          onClick={handleLinkedInClick}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "scale(1.1)";
            e.currentTarget.style.opacity = "0.8";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
            e.currentTarget.style.opacity = "1";
          }}
        >
          <IconBrandLinkedin size={48} color="#0A66C2" />
          <Text size="xs" fw={500}>
            LinkedIn
          </Text>
        </Stack>
        <Stack
          gap={4}
          align="center"
          style={{
            cursor: "pointer",
            transition: "transform 0.2s ease, opacity 0.2s ease",
          }}
          onClick={handleInstagramClick}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "scale(1.1)";
            e.currentTarget.style.opacity = "0.8";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
            e.currentTarget.style.opacity = "1";
          }}
        >
          <IconBrandInstagram size={48} color="#E4405F" />
          <Text size="xs" fw={500}>
            Instagram
          </Text>
        </Stack>
      </Group>

      {/* Instagram Share Confirmation Modal */}
      <Modal
        opened={instagramModalOpen}
        onClose={() => setInstagramModalOpen(false)}
        title="Share on Instagram"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            Instagram doesn't support pre-filling links, so we'll copy your
            referral link to your clipboard. Then you can open Instagram and
            paste it into your post or story.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button
              variant="subtle"
              onClick={() => setInstagramModalOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={executeInstagramShare}>Copy Link</Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};
